import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Grid,
  List,
  ListItem,
  Divider,
  Autocomplete,
  Chip,
  Paper
} from '@mui/material';
import { Delete, Add } from '@mui/icons-material';
import { DayBonus } from '../interfaces/timesheet';
import { format, addDays, startOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';


interface BonusDialogProps {
  open: boolean;
  onClose: () => void;
  day: string;
  dayLabel?: string;
  worker: string;
  bonuses: DayBonus[];
  salaryCodes: { code: number; name: string }[];
  onSave: (bonuses: DayBonus[]) => void;
  timesheetDate?: string;
}

const BonusDialog: React.FC<BonusDialogProps> = ({
  open,
  onClose,
  day,
  dayLabel,
  worker,
  bonuses = [],
  salaryCodes = [],
  onSave,
  timesheetDate
}) => {
  const DEFAULT_BONUS_HOURS = 8;
  const [currentBonuses, setCurrentBonuses] = useState<DayBonus[]>([]);
  const [newBonus, setNewBonus] = useState<{ code: number; name?: string } | null>(null);
  const [newHours, setNewHours] = useState<number | string>(DEFAULT_BONUS_HOURS);

  useEffect(() => {
    setCurrentBonuses(bonuses)
  }, [bonuses])

  const handleClose = () => {
    setCurrentBonuses(bonuses);
    setNewBonus(null);
    setNewHours(DEFAULT_BONUS_HOURS);
    onClose();
  };

  const handleAddBonus = () => {
    if (!newBonus) return;

    setCurrentBonuses([
      ...currentBonuses,
      {
        code: newBonus.code,
        name: newBonus.name,
        hours: typeof newHours === "string" ? parseInt(newHours, 10) : newHours
      }
    ]);
    setNewBonus(null);
    setNewHours(DEFAULT_BONUS_HOURS);
  };

  const handleRemoveBonus = (index: number) => {
    const updated = [...currentBonuses];
    updated.splice(index, 1);
    setCurrentBonuses(updated);
  };

  const handleUpdateHours = (index: number, hours: number | string) => {
    const updated = [...currentBonuses];
    updated[index].hours = hours;
    setCurrentBonuses(updated);
  };

  const handleSave = () => {
    onSave(currentBonuses);
    handleClose();
  };

  const formattedDate = timesheetDate ?
    format(
      addDays(
        startOfWeek(new Date(timesheetDate), { locale: fr }),
        ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'].indexOf(day.toLowerCase())
      ),
      'dd MMM',
      { locale: fr }
    ) : '';

  type Hours = number | string | null | undefined;

  const toHours = (h: Hours): number => {
    if (h == null) return 0;
    if (typeof h === 'number') return Number.isFinite(h) ? h : 0;
    // support " 1.5 ", "1,5", etc.
    const n = parseFloat(String(h).trim().replace(',', '.'));
    return Number.isNaN(n) ? 0 : n;
  };

  // Disable until some bonus has hours > 0
const hasEmptyOrZero = () =>
  currentBonuses.some(bonus => toHours(bonus.hours) <= 0);


  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {dayLabel || day} {formattedDate && `- ${formattedDate}`} - {worker}
      </DialogTitle>
      <DialogContent dividers>
        {/* Current Bonuses Section with enhanced styling */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 'medium' }}>
              Codes salaires actuels
            </Typography>
            <Chip
              label={`${currentBonuses.length} élément(s)`}
              size="small"
              color="primary"
              sx={{ ml: 2 }}
            />
          </Box>

          {currentBonuses.length === 0 ? (
            <Box sx={{
              py: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundColor: 'background.default',
              borderRadius: 1
            }}>
              <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
                pas de code salaire ajouté.
              </Typography>
            </Box>
          ) : (
            <List sx={{
              bgcolor: 'background.default',
              borderRadius: 1,
              maxHeight: '100%',
              minHeight: "100%",
              overflow: 'auto'
            }}>
              {currentBonuses.map((bonus, index) => (
                <React.Fragment key={index}>
                  <ListItem sx={{ py: 1 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={5} md={5}>
                        <Chip
                          label={`${bonus.code} - ${bonus.name || "Unknown Bonus"}`}
                          color="primary"
                          variant="outlined"
                          sx={{ maxWidth: '100%' }}
                        />
                      </Grid>
                      <Grid item xs={10} sm={4} md={4}>
                        <TextField
                          label="Hours"
                          type="number"
                          value={bonus.hours}
                          onChange={(e) => handleUpdateHours(index, e.target.value)}
                          size="small"
                          inputProps={{ min: 0.25, step: 0.25, max: 24 }}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={2} sm={3} md={3}>
                        <IconButton
                          onClick={() => handleRemoveBonus(index)}
                          color="error"
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </ListItem>
                  {index < currentBonuses.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>

        {/* Add New Bonus Section with distinct styling */}
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: 2,
            bgcolor: 'primary.lighter',
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'primary.main'
          }}
        >
          <Typography variant="h6" color="primary.dark" sx={{ mb: 2, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
            <Add fontSize="small" sx={{ mr: 1 }} />
            Ajouter d'autres codes salaires
          </Typography>

          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={5}>
              <Autocomplete
                id="salary-code-select"
                options={salaryCodes.filter(code =>
                  !currentBonuses.some(bonus => bonus.code === code.code)
                )}
                value={newBonus}
                onChange={(_, value) => setNewBonus(value)}
                getOptionLabel={(option) => `${option.code} - ${option.name}`}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Saisir un code salaire"
                    fullWidth
                    variant="filled"
                    sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                  />
                )}
                noOptionsText="Pas de code slaire trouvé"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Heures"
                type="number"
                value={newHours}
                onChange={(e) => {
                  return e.target.value.trim() === "" ? setNewHours("") : setNewHours(Number(e.target.value))
                }}
                fullWidth
                variant="filled"
                sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                inputProps={{ min: 0.25, step: 0.25, max: 24 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddBonus}
                disabled={!newBonus || newHours === ""}
                fullWidth
                sx={{ py: 1 }}
              >
                Ajouter
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={hasEmptyOrZero()}>
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BonusDialog;
